import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import type {
  ColDef,
  ColGroupDef,
  GridOptions,
  RowSelectionOptions,
  ValueFormatterParams,
  GetContextMenuItemsParams,
  MenuItemDef,
  GridReadyEvent,
} from 'ag-grid-community';
import { themeQuartz } from 'ag-grid-community';
import { clsx } from 'clsx';
import { OrderFlowChart } from '@/components/execution/OrderFlowChart';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  BarChart, Bar,
} from 'recharts';

// ══════════════════════════════════════════════════════════════
// THEME — identical to BBookPage
// ══════════════════════════════════════════════════════════════
const gridTheme = themeQuartz.withParams({
  backgroundColor: '#232326',
  browserColorScheme: 'dark',
  chromeBackgroundColor: { ref: 'foregroundColor', mix: 0.11, onto: 'backgroundColor' },
  fontFamily: { googleFont: 'IBM Plex Mono' },
  fontSize: 12,
  foregroundColor: '#FFF',
  headerFontSize: 14,
});

// ══════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════
// Fallback shown when an order has no identified NexRisk submitter — i.e. it
// originated outside NexRisk (terminal-placed, LP-side, etc.) or the NOS
// arrived before C++ started stamping `submitted_by`. Orders placed through
// the DOM Trader / hedge engine carry submitted_by on the NOS_SENT event.
const UNKNOWN_SUBMITTER = '—';

// Live grid row cap. The seed pages up to this and the 3s sync evicts past it,
// so both ends of the pipeline agree on one number instead of drifting apart.
const SEED_ROW_CAP = 500;

// States that mean the order reached the LP and filled. hedge_records reports
// HEDGED for a completed hedge and CLOSED/CLOSING once unwound; the live AE path
// reports FILLED. Counting only 'FILLED' left every header stat at zero as soon as
// rows were seeded from hedge_records.
const FILLED_STATES = new Set(['FILLED', 'HEDGED', 'PARTIAL', 'CLOSED', 'CLOSING']);
const WS_MAX_RETRIES = 8;

// ── Icons ──────────────────────────────────────────────────────
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
/* HIDDEN: TelegramIcon — unused while the share buttons are hidden; not removed
const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);
*/

// ══════════════════════════════════════════════════════════════
// TYPES
// ══════════════════════════════════════════════════════════════
// TE publishes TRADE_CAPTURE_REPORT (35=AE) for every fill — not 35=8.
// Shape: { type, lp_id, data: TradeInfo.ToJson() }
interface TradeCaptureWsEvent {
  type: 'TRADE_CAPTURE_REPORT';
  lp_id: string;
  data: {
    trade_report_id: string;  // tag 571 — our row key
    order_id: string;         // tag 37  — TE order ID
    exec_id: string;
    symbol: string;           // tag 55
    side: string;             // "BUY" | "SELL" — already mapped in C++
    last_qty: number;         // tag 32
    last_px: number;          // tag 31
    account: string;          // tag 1
    transact_time: string;    // tag 60 — FIX format YYYYMMDD-HH:MM:SS.mmm
    security_exchange: string;// tag 207
    ex_destination: string;   // tag 100
    security_id: string;      // tag 48
    canonical_symbol: string;
    trd_type: number;
    commission: number;
    received_ts: number;      // epoch ms
    // Correlated by C++ LookupNOS — present when DB lookup succeeded
    cl_ord_id?: string;       // tag 11 — our ClOrdID
    nos_sent_ms?: number;     // real UTC epoch ms when NOS was sent
  };
}

// NOS_SENT — published by BuildAndSendNOS, but nexrisk_service overwrites
// the type to EXECUTION_REPORT for all events on the "execution" ZMQ topic.
interface NosWsEvent {
  type: 'EXECUTION_REPORT';
  lp_id: string;
  cl_ord_id: string;
  symbol: string;
  side: string;
  qty: number;
  ord_type: string;
  tif: string;
  timestamp_ms: number;
}

export interface ExecutionReportRow {
  // Primary key — TE trade report ID (tag 571)
  trade_report_id: string;
  // Correlated from NOS (matched by symbol+side+time within 500ms window)
  clord_id: string;         // tag 11 — empty if NOS not yet correlated
  nos_time: string;         // ":ss.mmm" or "—"
  fill_time: string;        // confirm/AE-receive instant (the value RT is measured to) or "—"
  round_trip_ms: number | null;
  // Status — always FILLED for AE; PENDING if NOS sent but no AE yet
  te_status: 'FILLED' | 'HEDGED' | 'PENDING' | 'FAILED' | 'REJECTED' | 'CANCELLED' | 'ERROR' | 'B_BOOK' | 'PARTIAL' | 'ESCALATED' | 'CLOSED' | 'CLOSING' | 'UNKNOWN';
  user: string;
  order_id: string;         // tag 37 — TE order ID
  exec_id: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  ord_type: 'MKT' | 'LMT' | 'STP' | 'STPLMT';
  tif: 'GTC' | 'DAY' | 'IOC' | 'FOK' | 'GTD';
  order_qty: number;        // from NOS tag 38
  fill_px: number;          // tag 31
  fill_qty: number;         // tag 32
  commission: number;       // tag 12
  route: string;            // tag 100
  security_exchange: string;// tag 207
  security_id: string;      // tag 48
  settl_date: string;       // tag 64
  account: string;          // tag 1
  transact_time: string;    // tag 60 — FIX format
  lp_id: string;
  // Hedging strategy — populated by hedge.fill WS event, null for manual executions
  rule_id:   number | null;
  rule_name: string | null;
}
interface HedgeExecutionRow {
  record_id: number;
  clord_id: string;
  symbol: string;
  direction: 'LONG' | 'SHORT' | '';
  status: string;
  hedge_state: string;
  hedge_volume_lp: string;
  hedge_volume_mt5: string;
  lp_fill_volume_lp: string;
  lp_fill_volume_mt5: string;
  client_fill_price: string;
  lp_fill_price: string;
  raw_feed_price: string;
  net_revenue_pips: string;
  net_revenue_usd: string;
  lp_position_id: string;
  position_id: number;
  login_id: number;
  feed_lp_id: string;
  hedging_lp_id: string;
  rule_name: string;
  rule_id: number | null;
  escalation_reason: string;
  rejection_code: string;
  execution_source: string;
  submitted_by: string | null;   // NexRisk operator from C++ NOS stamp; null for pre-stamp/external rows
  account: string;
  route: string;
  security_exchange: string;
  security_id: string;
  dispatched_ms: number;
  confirmed_ms: number;
  escalated_ms: number;
  closed_ms: number;
}

interface LPStatus {
  lp_id: string;
  lp_name?: string;
  trading_session?: string;
  state?: string;
}

interface FIXPosition {
  position_id?: string;
  side?: 'LONG' | 'SHORT';
  net_qty?: number;
  [key: string]: unknown;
}

type WsStatus = 'connecting' | 'live' | 'reconnecting' | 'error';

const WS_BADGE: Record<WsStatus, { color: string; label: string }> = {
  connecting:   { color: '#e0a020', label: 'Connecting…'   },
  live:         { color: '#66e07a', label: 'Live'           },
  reconnecting: { color: '#e0a020', label: 'Reconnecting…' },
  error:        { color: '#ff5c5c', label: 'Disconnected'  },
};

// ══════════════════════════════════════════════════════════════
// LOOKUP / DECODE
// ══════════════════════════════════════════════════════════════
function mapOrdType(code?: string): ExecutionReportRow['ord_type'] {
  const map: Record<string, ExecutionReportRow['ord_type']> = {
    '1': 'MKT', '2': 'LMT', '3': 'STP', '4': 'STPLMT',
  };
  return map[code ?? ''] ?? 'MKT';
}

function mapTIF(code?: string): ExecutionReportRow['tif'] {
  const map: Record<string, ExecutionReportRow['tif']> = {
    '0': 'DAY', '1': 'GTC', '3': 'IOC', '4': 'FOK', '6': 'GTD',
  };
  return map[code ?? ''] ?? 'GTC';
}

// ══════════════════════════════════════════════════════════════
// TIMESTAMP HELPERS
// ══════════════════════════════════════════════════════════════
function parseTimestamp(ts: string): number {
  // FIX format: YYYYMMDD-HH:MM:SS.mmm
  const m = ts.match(/^(\d{4})(\d{2})(\d{2})-(\d{2}):(\d{2}):(\d{2})\.(\d{3})/);
  if (!m) return 0;
  return new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}.${m[7]}Z`).getTime();
}

function formatSsMs(ts: string): string {
  // Extract ":ss.mmm" from full FIX timestamp
  const match = ts.match(/:(\d{2}\.\d{3})/);
  return match ? `:${match[1]}` : ts;
}

// Convert epoch ms → FIX timestamp string (YYYYMMDD-HH:MM:SS.mmm)
// Used to normalise the C++ timestamp_ms field so buildRow / formatSsMs work
function msToFixTimestamp(ms: number): string {
  const d = new Date(ms);
  const p2 = (n: number) => n.toString().padStart(2, '0');
  const p3 = (n: number) => n.toString().padStart(3, '0');
  return (
    `${d.getUTCFullYear()}${p2(d.getUTCMonth() + 1)}${p2(d.getUTCDate())}` +
    `-${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}:${p2(d.getUTCSeconds())}` +
    `.${p3(d.getUTCMilliseconds())}`
  );
}

// ══════════════════════════════════════════════════════════════
// NOS TRACKER — pending orders waiting for AE correlation
// key: clord_id → the NOS for that exact order.
// Keyed on clord_id (unique per order), NOT symbol|side: rapid same
// symbol+side orders (scalper at 5s) collide on a symbol|side key and
// orphan all but the newest pending row, which then never clears until
// a manual page refresh re-seeds from the DB. clord_id is 1:1 with the
// order and the AE fill carries it back (C++ LookupNOS embeds cl_ord_id),
// so fills clear the exact pending row deterministically, at any rate.
// ══════════════════════════════════════════════════════════════
interface NosRecord {
  clord_id: string;
  symbol: string;
  side: string;       // "BUY" | "SELL"
  order_qty: number;
  ord_type: string;   // FIX tag 40 code
  tif: string;        // FIX tag 59 code
  nos_ts: number;     // epoch ms
  submitted_by: string | null;  // NexRisk operator who placed the order, null for external orders
}

// Build a grid row from a TRADE_CAPTURE_REPORT (35=AE) event.
// nosRecord: correlated NOS from nosMap, or null if not yet received.
function buildRowFromAE(
  ae: TradeCaptureWsEvent['data'],
  lp_id: string,
  nosRecord: NosRecord | null
): ExecutionReportRow {
  // Prefer DB-correlated fields embedded by C++ LookupNOS — these are reliable.
  // Fall back to in-memory nosRecord (symbol|side map) when DB unavailable.
  const corr_clord_id  = ae.cl_ord_id   || nosRecord?.clord_id  || '';
  const corr_nos_ts    = ae.nos_sent_ms  ?? nosRecord?.nos_ts    ?? null;

  // RT: both nos_sent_ms and received_ts are NexRisk internal epoch ms.
  // For live WS rows received_ts is stamped by C++ at exact AE receive time — accurate.
  // For DB-loaded historical rows received_ts is the audit-table write time, which can
  // lag the actual receive by several seconds.  Cap at 5000ms: any legitimate FIX
  // market-order RT is well under 5s; values above that are DB timestamp artefacts
  // and should be treated as unknown (null → shows as '—' in grid, excluded from charts).
  const fill_ts = ae.received_ts || 0;
  const round_trip_ms  = (corr_nos_ts && fill_ts && fill_ts > corr_nos_ts && (fill_ts - corr_nos_ts) < 5000)
    ? (fill_ts - corr_nos_ts)
    : null;

  return {
    trade_report_id: ae.trade_report_id,
    clord_id:        corr_clord_id,
    nos_time:        corr_nos_ts ? formatSsMs(msToFixTimestamp(corr_nos_ts)) : '—',
    fill_time:       fill_ts ? formatSsMs(msToFixTimestamp(fill_ts)) : '—',
    round_trip_ms,
    te_status:       'FILLED',
    user:            nosRecord?.submitted_by ?? UNKNOWN_SUBMITTER,
    order_id:        ae.order_id,
    exec_id:         ae.exec_id,
    symbol:          ae.symbol,
    side:            ae.side === 'SELL' ? 'SELL' : 'BUY',
    ord_type:        nosRecord ? mapOrdType(nosRecord.ord_type) : 'MKT',
    tif:             nosRecord ? mapTIF(nosRecord.tif)          : 'GTC',
    order_qty:       nosRecord?.order_qty ?? 0,
    fill_px:         ae.last_px,
    fill_qty:        ae.last_qty,
    commission:      ae.commission,
    route:           ae.ex_destination,
    security_exchange: ae.security_exchange,
    security_id:     ae.security_id,
    settl_date:      '',   // not in AE from TE
    account:         ae.account,
    transact_time:   ae.transact_time,
    lp_id,
    rule_id:   null,
    rule_name: null,
  };
}

// Build a row from a risk.hedge_records entry (hedge-executions endpoint).
// hedge_records is the order-lifecycle source of truth: it includes orders TE
// never confirmed (FAILED), rejected, errored, or b-booked — states the AE-only
// path could not represent. status is already mapped server-side.
// Map a /api/v1/hedge/records entry onto the HedgeExecutionRow shape that
// buildRowFromHedge expects. The two endpoints read the same table but use
// different field names, and hedge/records returns ISO timestamps rather than ms.
// Fields hedge/records does not carry (execution_source, submitted_by, account,
// route, security_*) fall back to empty — the WS fills them for live rows.
const asStr = (v: unknown): string => (v == null ? '' : String(v));
const isoToMs = (v: unknown): number => {
  if (v == null) return 0;
  const t = Date.parse(String(v));
  return Number.isNaN(t) ? 0 : t;
};

function hedgeRecordToExecutionRow(r: Record<string, unknown>): HedgeExecutionRow {
  const state = asStr(r.hedge_state);
  return {
    record_id:         Number(r.record_id ?? 0),
    clord_id:          asStr(r.clord_id),
    symbol:            asStr(r.mt5_symbol),
    direction:         (r.direction === 'LONG' || r.direction === 'SHORT') ? r.direction : '',
    status:            state,
    hedge_state:       state,
    hedge_volume_lp:   asStr(r.hedge_volume_lp),
    hedge_volume_mt5:  asStr(r.hedge_volume_mt5),
    lp_fill_volume_lp: asStr(r.lp_fill_volume_lp),
    lp_fill_volume_mt5: asStr(r.lp_fill_volume_mt5),
    client_fill_price: asStr(r.client_fill_price),
    lp_fill_price:     asStr(r.lp_hedge_fill_price_lp ?? r.lp_fill_price),
    raw_feed_price:    asStr(r.raw_feed_price),
    net_revenue_pips:  asStr(r.net_revenue_pips),
    net_revenue_usd:   asStr(r.net_revenue_usd),
    lp_position_id:    asStr(r.lp_position_id),
    position_id:       Number(r.position_id ?? 0),
    login_id:          Number(r.login_id ?? 0),
    feed_lp_id:        asStr(r.feed_lp_id),
    hedging_lp_id:     asStr(r.hedging_lp_id),
    rule_name:         asStr(r.rule_name),
    rule_id:           r.rule_id == null ? null : Number(r.rule_id),
    escalation_reason: asStr(r.escalation_reason),
    rejection_code:    asStr(r.rejection_code),
    execution_source:  asStr(r.execution_source),
    submitted_by:      r.submitted_by == null ? null : String(r.submitted_by),
    account:           asStr(r.account),
    route:             asStr(r.route),
    security_exchange: asStr(r.security_exchange),
    security_id:       asStr(r.security_id),
    dispatched_ms:     isoToMs(r.dispatched_at),
    confirmed_ms:      isoToMs(r.confirmed_at),
    escalated_ms:      isoToMs(r.escalated_at),
    closed_ms:         isoToMs(r.closed_at),
  };
}

// Fetch hedge records, paged. page_size is capped at 200 server-side, so a single
// call silently truncates history. Page 1 reports `total`; the rest are fetched in
// parallel up to `cap` — which is the grid's own row cap, since anything beyond it
// would be evicted by the sync effect on arrival.
// Returns null only if page 1 fails outright; partial pages are tolerated.
const HEDGE_PAGE_SIZE = 200;

// Mount starts two independent consumers of this data in the same tick - the
// history seed and the enrichment map - and the 60s poll re-enters it later.
// Uncoalesced that is two complete page-walks in parallel on every mount,
// against a backend on a remote host. One in-flight walk is now shared by all
// callers, and a successful result is reused for a few seconds so the two
// mount-time callers cost one walk between them. Failures are never cached, so
// a failed seed still retries exactly as before.
let hedgeInflight: Promise<Record<string, unknown>[] | null> | null = null;
let hedgeCache: { at: number; value: Record<string, unknown>[] } | null = null;
const HEDGE_CACHE_TTL_MS = 5_000;

async function fetchHedgeRecordsPaged(cap: number): Promise<Record<string, unknown>[] | null> {
  if (hedgeCache && Date.now() - hedgeCache.at < HEDGE_CACHE_TTL_MS) return hedgeCache.value;
  if (hedgeInflight) return hedgeInflight;

  const p = fetchHedgeRecordsPagedUncached(cap);
  hedgeInflight = p;
  try {
    const value = await p;
    if (value !== null) hedgeCache = { at: Date.now(), value };
    return value;
  } finally {
    if (hedgeInflight === p) hedgeInflight = null;
  }
}

async function fetchHedgeRecordsPagedUncached(cap: number): Promise<Record<string, unknown>[] | null> {
  const getPage = async (page: number): Promise<{ list: Record<string, unknown>[]; total: number } | null> => {
    const ac = new AbortController();
    const killer = setTimeout(() => ac.abort(), 8000);
    try {
      const r = await fetch(
        `/api/v1/hedge/records?page=${page}&page_size=${HEDGE_PAGE_SIZE}`,
        { signal: ac.signal },
      );
      if (!r.ok) return null;
      const j = await r.json();
      return { list: (j?.data ?? []) as Record<string, unknown>[], total: Number(j?.total ?? 0) };
    } catch {
      return null;
    } finally {
      clearTimeout(killer);
    }
  };

  const first = await getPage(1);
  if (!first) return null;

  const out = [...first.list];
  const wanted = first.total > 0 ? Math.min(first.total, cap) : out.length;
  const pageCount = Math.ceil(wanted / HEDGE_PAGE_SIZE);

  if (pageCount > 1) {
    const rest = await Promise.all(
      Array.from({ length: pageCount - 1 }, (_, i) => getPage(i + 2)),
    );
    for (const p of rest) if (p) out.push(...p.list);
  }
  return out;
}

function buildRowFromHedge(
  h: HedgeExecutionRow,
  lp_id: string
): ExecutionReportRow {
  const nos_ms   = h.dispatched_ms || 0;
  const conf_ms  = h.confirmed_ms  || 0;
  const round_trip_ms =
    (nos_ms && conf_ms && conf_ms > nos_ms && (conf_ms - nos_ms) < 5000)
      ? (conf_ms - nos_ms)
      : null;

  return {
    trade_report_id: h.clord_id ? `pending_${h.clord_id}` : `hedge_${h.record_id}`,
    clord_id:        h.clord_id || '',
    nos_time:        nos_ms ? formatSsMs(msToFixTimestamp(nos_ms)) : '—',
    fill_time:       conf_ms ? formatSsMs(msToFixTimestamp(conf_ms)) : '—',
    round_trip_ms,
    te_status:       (h.status as ExecutionReportRow['te_status']) || 'UNKNOWN',
    user:            h.execution_source === 'automated'
                       ? 'Hedge Engine'
                       : (h.submitted_by || h.execution_source || UNKNOWN_SUBMITTER),
    order_id:        h.lp_position_id || '',
    exec_id:         '',
    symbol:          h.symbol || '',
    side:            h.direction === 'SHORT' ? 'SELL' : 'BUY',
    ord_type:        'MKT',
    tif:             'GTC',
    order_qty:       parseFloat(h.hedge_volume_lp || '0'),
    fill_px:         parseFloat(h.lp_fill_price   || '0'),
    fill_qty:        parseFloat(h.lp_fill_volume_lp || '0'),
    commission:      0,
    route:           h.route,
    security_exchange: h.security_exchange,
    security_id:     h.security_id,
    settl_date:      '',
    account:         h.account,
    transact_time:   msToFixTimestamp(nos_ms),
    lp_id,
    rule_id:   h.rule_id ?? null,
    rule_name: h.rule_name ?? null,
  };
}

// Build a PENDING row when we've sent a NOS but no AE received yet
// FIX OrdStatus (tag 39) → display status. Returns null for statuses that carry no
// state change, so an intermediate frame never overwrites a terminal one.
function mapOrdStatusToTeStatus(ordStatus: string): ExecutionReportRow['te_status'] | null {
  switch (ordStatus) {
    case '2': return 'FILLED';      // terminal fill
    case '1': return 'PARTIAL';
    case '8': return 'REJECTED';
    case '4': return 'CANCELLED';
    case '0':
    case 'A': return 'PENDING';
    default:  return null;
  }
}

// Build or update a fill row from a FIX ExecutionReport.
//
// The ER is the only message with a per-order key. TE omits tag 11 on the AE and
// tag 37 is the position id under netting, so an AE cannot be tied to the order
// that produced it — which is why fills built from AE could not retire their
// pending row, resolve their submitter, or find their strategy name, and why DOM
// Trader orders never appeared at all despite reaching TE correctly.
//
// Keyed `pending_<cl_ord_id>`, the same id buildPendingRow and buildRowFromHedge
// derive, so the NOS row, the seeded row and the fill are all one row rather than
// three. Quantities here are LP-side (tags 38/32/14) — notional, not MT5 lots.
function buildRowFromExecReport(
  er: Record<string, unknown>,
  lp_id: string,
  nosRecord: NosRecord | null,
  existing: ExecutionReportRow | undefined,
): ExecutionReportRow {
  const clordId = String(er.cl_ord_id ?? er.clord_id ?? '');
  const ts      = Number(er.timestamp_ms ?? 0) || 0;
  const nosTs   = nosRecord?.nos_ts ?? null;
  const mapped  = mapOrdStatusToTeStatus(String(er.ord_status ?? ''));
  const isFill  = mapped === 'FILLED' || mapped === 'PARTIAL';

  const lastQty = Number(er.last_qty ?? 0) || 0;
  const cumQty  = Number(er.cum_qty  ?? 0) || 0;
  const lastPx  = Number(er.last_px  ?? 0) || 0;

  // Same 5s sanity bound as the AE path: anything larger is a timestamp artefact
  // rather than a real round trip, and is better shown as unknown.
  const rt = (nosTs && ts && ts > nosTs && ts - nosTs < 5000)
    ? ts - nosTs
    : existing?.round_trip_ms ?? null;

  const base: ExecutionReportRow = existing ?? {
    trade_report_id: `pending_${clordId}`,
    clord_id:        clordId,
    nos_time:        nosTs ? formatSsMs(msToFixTimestamp(nosTs)) : '—',
    fill_time:       '—',
    round_trip_ms:   null,
    te_status:       'PENDING',
    user:            nosRecord?.submitted_by ?? UNKNOWN_SUBMITTER,
    order_id:        '',
    exec_id:         '',
    symbol:          String(er.symbol ?? ''),
    side:            er.side === 'SELL' ? 'SELL' : 'BUY',
    ord_type:        nosRecord ? mapOrdType(nosRecord.ord_type) : 'MKT',
    tif:             nosRecord ? mapTIF(nosRecord.tif) : 'GTC',
    order_qty:       0,
    fill_px:         0,
    fill_qty:        0,
    commission:      0,
    route:           '',
    security_exchange: '',
    security_id:     '',
    settl_date:      '',
    account:         '',
    transact_time:   '',
    lp_id,
    rule_id:   null,
    rule_name: null,
  };

  return {
    ...base,
    trade_report_id: `pending_${clordId}`,
    clord_id:        clordId,
    symbol:          String(er.symbol ?? base.symbol),
    side:            er.side === 'SELL' ? 'SELL' : (er.side === 'BUY' ? 'BUY' : base.side),
    te_status:       mapped ?? base.te_status,
    user:            nosRecord?.submitted_by ?? base.user,
    order_id:        String(er.order_id ?? '') || base.order_id,
    exec_id:         String(er.exec_id  ?? '') || base.exec_id,
    order_qty:       cumQty > 0 ? cumQty : base.order_qty,
    fill_qty:        isFill ? (cumQty || lastQty) : base.fill_qty,
    fill_px:         lastPx > 0 ? lastPx : base.fill_px,
    fill_time:       isFill && ts ? formatSsMs(msToFixTimestamp(ts)) : base.fill_time,
    round_trip_ms:   isFill ? rt : base.round_trip_ms,
    transact_time:   ts ? msToFixTimestamp(ts) : base.transact_time,
    lp_id:           lp_id || base.lp_id,
  };
}

function buildPendingRow(nos: NosRecord, lp_id: string): ExecutionReportRow {
  return {
    trade_report_id: `pending_${nos.clord_id}`,
    clord_id:        nos.clord_id,
    nos_time:        formatSsMs(msToFixTimestamp(nos.nos_ts)),
    fill_time:       '—',
    round_trip_ms:   null,
    te_status:       'PENDING',
    user:            nos.submitted_by ?? UNKNOWN_SUBMITTER,
    order_id:        '',
    exec_id:         '',
    symbol:          nos.symbol,
    side:            nos.side === 'SELL' ? 'SELL' : 'BUY',
    ord_type:        mapOrdType(nos.ord_type),
    tif:             mapTIF(nos.tif),
    order_qty:       nos.order_qty,
    fill_px:         0,
    fill_qty:        0,
    commission:      0,
    route:           '',
    security_exchange: '',
    security_id:     '',
    settl_date:      '',
    account:         '',
    transact_time:   msToFixTimestamp(nos.nos_ts),
    lp_id,
    rule_id:   null,
    rule_name: null,
  };
}

// ══════════════════════════════════════════════════════════════
// SIDE PANEL — generateExplanation (replaceable with LLM later)
// ══════════════════════════════════════════════════════════════
function generateExplanation(row: ExecutionReportRow): string {
  const lines: string[] = [];

  lines.push('ORDER SUMMARY');
  lines.push(`Direction : ${row.side} ${row.order_qty > 0 ? row.order_qty.toLocaleString() : '—'} ${row.symbol} (${row.ord_type}, ${row.tif})`);
  lines.push(`Status    : ${row.te_status}`);

  if (row.te_status === 'PENDING') {
    lines.push('Order sent to TE — awaiting fill confirmation (35=AE).');
  } else if (row.te_status === 'FAILED' || row.te_status === 'REJECTED' || row.te_status === 'ERROR') {
    const reason =
      row.te_status === 'FAILED'   ? 'Hedge not confirmed by LP within timeout — OrderCancelRequest sent.' :
      row.te_status === 'REJECTED' ? 'Order rejected by LP.' :
                                     'Internal error during hedge processing.';
    lines.push(reason);
    lines.push('No fill — order did not execute.');
  } else {
    const fillPx = row.fill_px > 0 ? row.fill_px.toFixed(5) : '—';
    const fillQty = row.fill_qty > 0 ? row.fill_qty.toLocaleString() : '—';
    lines.push(`Fill Px   : ${fillPx}  (tag 31)`);
    lines.push(`Fill Qty  : ${fillQty}  (tag 32)`);
    if (row.commission > 0) lines.push(`Commission: ${row.commission}`);
  }

  lines.push('');
  lines.push('OUR REFERENCE');
  lines.push(`Trade Rpt ID: ${row.trade_report_id}  (tag 571)`);
  lines.push(`ClOrdID     : ${row.clord_id || '—'}  (tag 11 — correlated from NOS)`);
  lines.push(`TE OrderID  : ${row.order_id || '—'}  (tag 37)`);
  lines.push(`Submitted   : ${row.user}`);

  lines.push('');
  lines.push('ROUTING');
  lines.push(`Account   : ${row.account || '—'}`);
  lines.push(`Route     : ${row.route || '—'}`);
  lines.push(`Exchange  : ${row.security_exchange || '—'}`);
  lines.push(`Security  : ${row.security_id || '—'}`);

  lines.push('');
  lines.push('LIFECYCLE TIMING');
  lines.push(`NOS sent   : ${row.nos_time.padEnd(10)}  (we submitted the order)`);
  const filledLabel = (row.te_status === 'FAILED' || row.te_status === 'REJECTED' || row.te_status === 'ERROR')
    ? `Escalated  : ${row.transact_time ? formatSsMs(row.transact_time) : '—'}  (no fill — timeout/reject)`
    : `TE filled  : ${row.fill_time.padEnd(10)}  (35=AE received)`;
  lines.push(filledLabel);
  lines.push(`Round trip : ${row.round_trip_ms !== null ? `${row.round_trip_ms}ms` : '—'}`);

  lines.push('');
  lines.push('TIME');
  lines.push(`Transact   : ${row.transact_time}  UTC`);

  return lines.join('\n');
}

// ══════════════════════════════════════════════════════════════
// LP SESSION STATE COLOR
// ══════════════════════════════════════════════════════════════
function lpStateColor(trading_session?: string): string {
  if (trading_session === 'LOGGED_ON')                                          return '#66e07a';
  if (trading_session === 'CONNECTING' || trading_session === 'RECONNECTING')   return '#e0a020';
  if (trading_session === 'DISCONNECTED')                                       return '#ff5c5c';
  return '#e0a020'; // unknown / stale initial state — amber, not red
}

// ══════════════════════════════════════════════════════════════
// VALUE FORMATTERS
// ══════════════════════════════════════════════════════════════
const fmtQty = (p: ValueFormatterParams) => {
  const v = p.value as number | null;
  if (v === null || v === undefined || v === 0) return '—';
  return Number(v).toLocaleString();
};

const fmtPx = (p: ValueFormatterParams) => {
  const v = p.value as number | null;
  if (v === null || v === undefined || v === 0) return '—';
  return Number(v).toFixed(5);
};

// ══════════════════════════════════════════════════════════════
// ANALYTICS CHART COMPONENTS
// ══════════════════════════════════════════════════════════════

// Returns true when transact_time (YYYYMMDD-HH:MM:SS.mmm) is from today (UTC).
function isTodayRow(transact_time: string): boolean {
  const prefix = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return transact_time.startsWith(prefix);
}

// Muted, non-fluorescent palette — intentionally matches BBookCharts aesthetic.
const STATUS_COLORS: Record<string, string> = {
  FILLED:    '#4a7c5e',  // muted sage green
  REJECTED:  '#7c4a68',  // muted rose
  PENDING:   '#5c4d7d',  // muted purple
  CANCELLED: '#5a5a65',  // muted slate grey
};

// ── Tooltip sub-components (hoisted to module scope — no remount on re-render) ──

interface LatencyEntry { symbol: string; time: string }
function LatencyOverTimeTooltip({ active, payload }: { active?: boolean; payload?: Array<{ value: number; payload: LatencyEntry }> }) {
  if (!active || !payload?.length) return null;
  const { value, payload: p } = payload[0];
  return (
    <div className="bg-[#232225] border border-[#555] rounded px-2 py-1 text-xs">
      <p className="text-[#999]">{p.symbol} @ {p.time}</p>
      <p className="text-white font-mono font-semibold">{value}ms</p>
    </div>
  );
}

interface LPEntry { lp: string; min: number; avg: number; max: number }
function LatencyByLPTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: LPEntry }> }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#232225] border border-[#555] rounded px-2 py-1 text-xs">
      <p className="text-white font-medium mb-1">{d.lp}</p>
      <p style={{ color: '#4a7c5e' }}>Min: <span className="font-mono">{d.min}ms</span></p>
      <p style={{ color: '#7eaacb' }}>Avg: <span className="font-mono">{d.avg}ms</span></p>
      <p style={{ color: '#b87333' }}>Max: <span className="font-mono">{d.max}ms</span></p>
    </div>
  );
}

interface StatusEntry { name: string; value: number }
function OrdersByStatusTooltip({ active, payload, total }: { active?: boolean; payload?: Array<{ payload: StatusEntry }>; total: number }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : '0';
  return (
    <div className="bg-[#232225] border border-[#555] rounded px-2 py-1 text-xs">
      <p style={{ color: STATUS_COLORS[d.name] ?? '#999' }} className="font-medium">{d.name}</p>
      <p className="text-white font-mono">{d.value} <span className="text-[#999]">({pct}%)</span></p>
    </div>
  );
}

// ── 1. Execution Latency Over Time ─────────────────────────────
function LatencyOverTimeChart({ rows }: { rows: ExecutionReportRow[] }) {
  const data = useMemo(() => (
    rows
      .filter(r => r.round_trip_ms !== null && isTodayRow(r.transact_time))
      .map(r => ({
        time:   r.transact_time.slice(9, 17),  // HH:MM:SS
        rt:     r.round_trip_ms as number,
        symbol: r.symbol,
      }))
      .sort((a, b) => a.time.localeCompare(b.time))
      .slice(-60)                               // cap at 60 most-recent fills
  ), [rows]);

  return (
    <div className="rounded p-2 flex flex-col overflow-hidden" style={{ backgroundColor: '#232225' }}>
      <div className="mb-1 shrink-0">
        <h4 className="text-xs font-semibold text-white">Latency Over Time</h4>
        <p className="text-[10px] text-[#bbb]">Round-trip ms · Today</p>
      </div>
      <ResponsiveContainer width="100%" height={192}>
        <LineChart data={data} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="#2e2e30" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 10, fill: '#ddd' }}
            interval="preserveStartEnd"
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#ddd' }}
            tickLine={false}
            axisLine={false}
            unit="ms"
          />
          <Tooltip content={<LatencyOverTimeTooltip />} cursor={{ stroke: '#444' }} />
          <Line
            type="monotone"
            dataKey="rt"
            stroke="#577a9e"
            strokeWidth={1.5}
            dot={{ r: 3, fill: '#577a9e', strokeWidth: 0 }}
            activeDot={{ r: 5, fill: '#7eaacb', strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── 2. Execution Latency by LP (stacked horizontal bar: min/avg-min/max-avg) ──
function LatencyByLPChart({ rows }: { rows: ExecutionReportRow[] }) {
  const data = useMemo(() => {
    const byLp: Record<string, number[]> = {};
    rows
      .filter(r => r.round_trip_ms !== null && isTodayRow(r.transact_time))
      .forEach(r => {
        if (!byLp[r.lp_id]) byLp[r.lp_id] = [];
        byLp[r.lp_id].push(r.round_trip_ms as number);
      });
    return Object.entries(byLp).map(([lp, vals]) => {
      const sorted = [...vals].sort((a, b) => a - b);
      const min    = sorted[0];
      const max    = sorted[sorted.length - 1];
      const avg    = Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
      return {
        lp,
        minVal:   min,
        midRange: avg - min,
        topRange: max - avg,
        // raw values used by tooltip
        min, avg, max,
      };
    });
  }, [rows]);

  // Compute Y-axis width dynamically so no LP name is ever clipped
  const yAxisWidth = Math.min(140, Math.max(64, ...data.map(d => d.lp.length * 7)));

  return (
    <div className="rounded p-2 flex flex-col overflow-hidden" style={{ backgroundColor: '#232225' }}>
      <div className="mb-1 shrink-0">
        <h4 className="text-xs font-semibold text-white">Latency by LP</h4>
        <p className="text-[10px] text-[#bbb]">Min / Avg / Max RT · Today</p>
      </div>
      <ResponsiveContainer width="100%" height={168}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 8, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="#2e2e30" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 10, fill: '#ddd' }}
            tickLine={false}
            axisLine={false}
            unit="ms"
          />
          <YAxis
            type="category"
            dataKey="lp"
            tick={{ fontSize: 10, fill: '#ddd' }}
            tickLine={false}
            axisLine={false}
            width={yAxisWidth}
          />
          <Tooltip content={<LatencyByLPTooltip />} cursor={{ fill: '#2a2a2c' }} />
          <Bar dataKey="minVal"   stackId="rt" fill="#4a7c5e" name="Min" radius={0}         />
          <Bar dataKey="midRange" stackId="rt" fill="#3d5a80" name="Avg" radius={0}         />
          <Bar dataKey="topRange" stackId="rt" fill="#b87333" name="Max" radius={[0,3,3,0]} />
        </BarChart>
      </ResponsiveContainer>
      <div className="flex justify-center gap-3 mt-1 shrink-0">
        {([['Min', '#4a7c5e'], ['Avg', '#3d5a80'], ['Max', '#b87333']] as [string, string][]).map(([label, color]) => (
          <div key={label} className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
            <span className="text-[10px] text-white">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 3. Orders by Status (donut) ─────────────────────────────────
function OrdersByStatusChart({ rows }: { rows: ExecutionReportRow[] }) {
  const { data, total } = useMemo(() => {
    const counts: Record<string, number> = { FILLED: 0, REJECTED: 0, PENDING: 0, CANCELLED: 0 };
    rows
      .filter(r => isTodayRow(r.transact_time))
      .forEach(r => {
        if      (r.te_status === 'FILLED')  counts.FILLED++;
        else if (r.te_status === 'PENDING') counts.PENDING++;
        else                                counts.CANCELLED++;
      });
    const data  = Object.entries(counts).map(([name, value]) => ({ name, value }));
    const total = data.reduce((s, d) => s + d.value, 0);
    return { data, total };
  }, [rows]);

  // Pass total via closure into a wrapper so the Tooltip can access it
  const TooltipWithTotal = useMemo(
    () => (props: { active?: boolean; payload?: Array<{ payload: StatusEntry }> }) =>
      <OrdersByStatusTooltip {...props} total={total} />,
    [total],
  );

  return (
    <div className="rounded p-2 flex flex-col overflow-hidden" style={{ backgroundColor: '#232225' }}>
      <div className="mb-1 shrink-0">
        <h4 className="text-xs font-semibold text-white">Orders by Status</h4>
        <p className="text-[10px] text-[#bbb]">{total} orders · Today</p>
      </div>
      <ResponsiveContainer width="100%" height={162}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius="42%"
            outerRadius="82%"
            paddingAngle={2}
            dataKey="value"
            stroke="none"
          >
            {data.map((entry, i) => (
              <Cell key={i} fill={STATUS_COLORS[entry.name] ?? '#555'} />
            ))}
          </Pie>
          <Tooltip content={<TooltipWithTotal />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap justify-center gap-x-2 gap-y-0.5 mt-1 shrink-0">
        {data.map((item, i) => (
          <div key={i} className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: STATUS_COLORS[item.name] ?? '#555' }} />
            <span className="text-[10px] text-white">
              {item.name}: <span className="font-semibold">{item.value}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 4. Volume by Symbol (vertical bar) ──────────────────────────
// Axis ticks: compact (16M, 750K) so large fill totals fit the axis width
const fmtVolTick = (v: number): string => {
  const a = Math.abs(v);
  if (a >= 1e9) return `${+(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${+(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${+(v / 1e3).toFixed(1)}K`;
  return String(v);
};

function VolumeBySymbolChart({ rows }: { rows: ExecutionReportRow[] }) {
  const data = useMemo(() => {
    const bySymbol: Record<string, number> = {};
    rows
      .filter(r => isTodayRow(r.transact_time) && r.fill_qty > 0)
      .forEach(r => { bySymbol[r.symbol] = (bySymbol[r.symbol] || 0) + r.fill_qty; });
    return Object.entries(bySymbol)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([symbol, vol]) => ({ symbol, vol }));
  }, [rows]);

  return (
    <div className="rounded p-2 flex flex-col overflow-hidden" style={{ backgroundColor: '#232225' }}>
      <div className="mb-1 shrink-0">
        <h4 className="text-xs font-semibold text-white">Volume by Symbol</h4>
        <p className="text-[10px] text-[#bbb]">Fill quantity · Today</p>
      </div>
      <ResponsiveContainer width="100%" height={215}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 24 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="#2e2e30" vertical={false} />
          <XAxis
            dataKey="symbol"
            tick={{ fontSize: 10, fill: '#ddd', angle: -35, textAnchor: 'end' }}
            tickLine={false}
            interval={0}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#ddd' }}
            tickLine={false}
            axisLine={false}
            width={40}
            tickFormatter={fmtVolTick}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#232225', border: '1px solid #555', borderRadius: 4, fontSize: 11 }}
            formatter={(val: number) => [Math.round(val).toLocaleString('en-US'), 'Volume']}
            labelStyle={{ color: '#fff' }}
            itemStyle={{ color: '#fff' }}
            cursor={{ fill: '#2a2a2c' }}
          />
          <Bar dataKey="vol" fill="#5c4d7d" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// COMPONENT
// ══════════════════════════════════════════════════════════════
export function ExecutionReportPage() {
  const gridRef  = useRef<AgGridReact<ExecutionReportRow>>(null);
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────
  const [rows,           setRows]           = useState<ExecutionReportRow[]>([]);
  const [lpStatuses,     setLpStatuses]     = useState<LPStatus[]>([]);
  const [wsStatus,       setWsStatus]       = useState<WsStatus>('connecting');
  const [wsError,        setWsError]        = useState<string | null>(null);
  const [seedError,      setSeedError]      = useState<string | null>(null);
  const [selectedRow,    setSelectedRow]    = useState<ExecutionReportRow | null>(null);
  const [copied,         setCopied]         = useState(false);
  const [chartsCollapsed, setChartsCollapsed] = useState(false);
  const [selectedLp,      setSelectedLp]      = useState<string>('all');
  const [showOrderFlow,   setShowOrderFlow]   = useState(false);

  // O(1) trade_report_id → row lookup for WS upserts
  const rowMapRef   = useRef<Map<string, ExecutionReportRow>>(new Map());
  // Secondary index: clord_id → trade_report_id (for hedge.fill correlation)
  const clordIdMapRef = useRef<Map<string, string>>(new Map());
  // Hedge records map: clord_id → {rule_id, rule_name}
  // Loaded from GET /api/v1/hedge/records on mount and refreshed every 30s.
  // Used to enrich fill rows with strategy names reliably, without depending
  // on the hedge.fill WS event (which may arrive out of order or not at all).
  const hedgeRuleMapRef = useRef<Map<string, { rule_id: number; rule_name: string | null }>>(new Map());
  // Pending hedge.fill payloads that arrived before the fill row was added.
  // Keyed by clord_id — applied when the matching fill row is stored.
  const pendingHedgeFillsRef = useRef<Map<string, { rule_id: number; rule_name: string | null }>>(new Map());
  // NOS correlation: symbol|side → most recent NOS within 500ms window
  const nosMapRef   = useRef<Map<string, NosRecord>>(new Map());
  const wsRef       = useRef<WebSocket | null>(null);
  const retryRef    = useRef(0);
  const mountedRef  = useRef(true);
  const timerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirror of selectedRow.trade_report_id. The WS handlers called setSelectedRow on
  // every single execution frame; the updater returned `prev` unchanged when nothing
  // matched, so React bailed out of re-rendering, but each call still queued and was
  // processed. At ~100 orders/sec across several frames per order that is a few
  // hundred needless setState calls per second in separate WS callbacks, which React
  // cannot batch. Reading this ref first makes the no-op case free.
  const selectedIdRef = useRef<string | null>(null);

  // Keep selectedIdRef in sync with the selected row
  useEffect(() => {
    selectedIdRef.current = selectedRow?.trade_report_id ?? null;
  }, [selectedRow]);

  // Keep rowMapRef and clordIdMapRef in sync whenever rows state changes
  useEffect(() => {
    const m = new Map<string, ExecutionReportRow>();
    const c = new Map<string, string>();
    for (const r of rows) {
      m.set(r.trade_report_id, r);
      if (r.clord_id) c.set(r.clord_id, r.trade_report_id);
    }
    rowMapRef.current    = m;
    clordIdMapRef.current = c;
  }, [rows]);

  // ── One-shot historical load on mount ──────────────────────
  // Fetches today's fills from the DB-backed audit trail.
  // Runs once. No polling. Failures are silent — WS fills in going forward.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        // 1. LP list — fetch all configured LPs (admin) merged with live session state
        // /api/v1/fix/admin/lp returns ALL LPs including disabled/disconnected ones
        // /api/v1/fix/status returns only currently running session states
        const [adminRes, statusRes] = await Promise.all([
          fetch('/api/v1/fix/admin/lp'),
          fetch('/api/v1/fix/status'),
        ]);
        if (cancelled) return;

        const adminData  = adminRes.ok  ? await adminRes.json()  : null;
        const statusData = statusRes.ok ? await statusRes.json() : null;

        // Build session state map from status response
        const rawLps = (statusData?.lps ?? statusData?.data?.lps ?? []) as Array<{ lp_id: string; state?: string; trading_session?: string }>;
        const stateMap = new Map<string, string>();
        if (Array.isArray(rawLps)) {
          rawLps.forEach(v => stateMap.set(v.lp_id, v.trading_session ?? v.state ?? ''));
        } else {
          Object.entries(rawLps as Record<string, Record<string, string>>).forEach(
            ([lp_id, v]) => stateMap.set(lp_id, v.trading_session ?? v.state ?? '')
          );
        }

        // All configured LPs as base, overlay live session state
        const configuredLps = (adminData?.data?.lps ?? adminData?.lps ?? []) as Array<{ lp_id: string; lp_name?: string; enabled?: boolean }>;
        // Any LP not in stateMap has no live session → DISCONNECTED
        const lps: LPStatus[] = configuredLps.length > 0
          ? configuredLps.map(lp => ({
              lp_id:           lp.lp_id,
              lp_name:         lp.lp_name,
              trading_session: stateMap.get(lp.lp_id) ?? 'DISCONNECTED',
            }))
          : rawLps.map(v => ({ lp_id: v.lp_id, trading_session: v.trading_session ?? v.state }));

        if (!cancelled) {
          setLpStatuses(lps);
          // Default selection to first LP if only one, else keep 'all'
          if (lps.length === 1) setSelectedLp(lps[0].lp_id);
        }

         // 2. Seed history from TWO sources, merged on trade_report_id.
        //
        // 2a. /api/v1/fix/lp/{lp}/hedge-executions — the FIX message store. This is
        //     the ONLY source containing manual / DOM Trader orders. hedge_records is
        //     written solely by HedgeDispatcher::InsertHedgeRecord, so it holds
        //     automated hedges and nothing else; seeding from it alone is what made
        //     DOM trades vanish from this blotter.
        //     The route exists end to end: BFF fix-bridge.ts:741 →
        //     FIXBridgeEndpoint.cpp:410 → GET_HEDGE_EXECUTIONS. It can time out under
        //     load because it proxies over the ZMQ command socket that order dispatch
        //     also uses, hence the 503 retry and the non-fatal catch.
        //
        // 2b. /api/v1/hedge/records — supplies rule_name and hedge_state, which the
        //     executions source does not carry. Applied second so it enriches the
        //     matching rows; buildRowFromHedge derives the same `pending_<clord_id>`
        //     key from both, so hedge orders merge rather than duplicate.
        //
        const seedMap = new Map<string, ExecutionReportRow>();
        let execFailed   = false;
        let recordFailed = false;

        // Start the hedge/records seed immediately rather than after the
        // executions seed completes. These two were sequential, so the whole
        // records fetch waited behind hedge-executions - which currently returns
        // HTTP 400 after ~5s (RCVTIMEO on the ZMQ command socket it shares with
        // order dispatch). That was five dead seconds on every mount before the
        // fast source was even requested.
        //
        // They still merge in the original order: executions first, records
        // second so they enrich the matching rows. Only the waiting overlaps.
        const recsPromise = fetchHedgeRecordsPaged(SEED_ROW_CAP);

        await Promise.allSettled(lps.map(async (lp) => {
          try {
            const url = `/api/v1/fix/lp/${lp.lp_id}/hedge-executions?limit=${SEED_ROW_CAP}`;
            // Bound each attempt. Unbounded, a stalled upstream held the mount
            // open for its full timeout; the page cannot render until this
            // settles, so it must fail fast rather than wait out the backend.
            const ac = new AbortController();
            const killer = setTimeout(() => ac.abort(), 2000);
            let res: Response;
            try {
              res = await fetch(url, { signal: ac.signal });
              if (res.status === 503) {
                await new Promise(r => setTimeout(r, 3000));
                if (cancelled) return;
                res = await fetch(url, { signal: ac.signal });
              }
            } finally {
              clearTimeout(killer);
            }
            if (!res.ok || cancelled) { execFailed = true; return; }
            const data = await res.json();
            const execRows: HedgeExecutionRow[] = data?.data ?? [];
            for (const h of execRows) {
              const row = buildRowFromHedge(h, lp.lp_id);
              seedMap.set(row.trade_report_id, row);
            }
          } catch { execFailed = true; }
        }));

        try {
          const recs = await recsPromise;
          if (!recs) {
            recordFailed = true;
          } else if (!cancelled) {
            // lp_id must match an entry in the LP dropdown or the row is filtered
            // out of both the grid and the header stats. hedge_records reports
            // hedging_lp_id, which is not guaranteed to use the same identifier as
            // the LP list, so fall back rather than trust it blindly.
            const knownLpIds = new Set(lps.map(l => l.lp_id));
            const resolveLp = (raw: string): string => {
              if (knownLpIds.has(raw)) return raw;
              return lps.length === 1 ? lps[0].lp_id : raw;
            };
            for (const r of recs) {
              const row = buildRowFromHedge(
                hedgeRecordToExecutionRow(r),
                resolveLp(String(r.hedging_lp_id ?? '')),
              );
              seedMap.set(row.trade_report_id, row);
            }
          }
        } catch { recordFailed = true; }

        const seedRows: ExecutionReportRow[] = Array.from(seedMap.values());
        const seedFailed = execFailed && recordFailed;

        if (!cancelled && seedRows.length === 0 && seedFailed) {
          // Do not leave the page showing "Waiting for orders" — that reads as
          // "no orders yet" when in fact history could not be loaded.
          setSeedError('Could not load order history. Live orders will still appear as they arrive.');
        }

        if (!cancelled && seedRows.length > 0) {
          seedRows.sort((a, b) =>
            parseTimestamp(b.transact_time) - parseTimestamp(a.transact_time)
          );
          setRows(seedRows);
          // Populate rowMapRef and clordIdMapRef for all seeded rows
          for (const row of seedRows) {
            rowMapRef.current.set(row.trade_report_id, row);
            if (row.clord_id) clordIdMapRef.current.set(row.clord_id, row.trade_report_id);
          }
          // Push into grid — hedge records polling effect will enrich with strategy names
          gridRef.current?.api?.applyTransactionAsync({ add: seedRows });
        }
      } catch { /* non-fatal */ }
    };

    load();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── WebSocket ───────────────────────────────────────────────
  const connectWs = useCallback(() => {
    if (!mountedRef.current) return;
    setWsStatus('connecting');
    setWsError(null);

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws/v1/fix/events`);
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current = 0;
      setWsStatus('live');
      // No subscribe frame. CBookPage opens this same endpoint without one and
      // receives DOM fills correctly, so the server pushes by default and an
      // explicit topic list can only narrow what arrives.
    };

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data as string) as Record<string, unknown>;

        // ── HEDGE FILL — strategy correlation ─────────────────
        // Must be checked FIRST — hedge.fill events arrive with
        // msg.type === 'EXECUTION_REPORT' (nexrisk_service overwrites type
        // for everything on the execution ZMQ topic), so checking topic
        // before type ensures we catch them before the fill handler does.
        // Carries rule_id and rule_name; correlated to grid rows via clord_id.
        if (msg.topic === 'hedge.fill') {
          const hd      = (msg.data ?? msg) as Record<string, unknown>;
          const clordId  = hd.clord_id  as string | undefined;
          const ruleId   = hd.rule_id   as number | null | undefined;
          const ruleName = hd.rule_name as string | null | undefined;
          if (!clordId || ruleId === undefined || ruleId === null) return;

          // Find the row via secondary clord_id index
          const tradeId = clordIdMapRef.current.get(clordId);
          if (!tradeId) {
            // Fill row hasn't arrived yet — store and apply when it does
            pendingHedgeFillsRef.current.set(clordId, { rule_id: ruleId, rule_name: ruleName ?? null });
            return;
          }
          const existing = rowMapRef.current.get(tradeId);
          if (!existing) return;

          const updated: ExecutionReportRow = {
            ...existing,
            rule_id:   ruleId,
            rule_name: ruleName ?? null,
          };
          rowMapRef.current.set(tradeId, updated);
          clordIdMapRef.current.set(clordId, tradeId);
          gridRef.current?.api?.applyTransactionAsync({ update: [updated] });
          return; // handled — do not fall through to type-based handlers

        // C++ publishes execution events under msg.type === 'EXECUTION_REPORT'
        // with the original payload wrapped under msg.data by NexRiskService.
        // The inner payload distinguishes the sub-type:
        //
        //   NOS_SENT  → has data.cl_ord_id (and data.type === 'NOS_SENT' per the
        //               most recent C++ change). Carries submitted_by stamped
        //               by the BFF from the authenticated session.
        //   AE fill   → has data.trade_report_id.
        //
        //     { type:"EXECUTION_REPORT", lp_id, data:{ cl_ord_id, symbol, side, qty,
        //       ord_type, tif, timestamp_ms, submitted_by, ... } }    ← NOS_SENT
        //     { type:"EXECUTION_REPORT", lp_id, data:{ trade_report_id, symbol,
        //       side, ... } }                                          ← AE fill
        //
        } else if (msg.type === 'EXECUTION_REPORT') {
          const lp_id   = msg.lp_id as string ?? '';
          // Fields may arrive wrapped under msg.data or flat on the envelope — the
          // FIX Bridge API doc documents the flat form. Reading only msg.data drops
          // flat frames with no row and no error. Same fallback the hedge.fill
          // branch above already uses.
          // Fields arrive under msg.data, flat on the envelope, or SPLIT ACROSS
          // BOTH. CBookPage reads `fill.x ?? msg.x` per field on this same stream
          // for exactly that reason, and it receives DOM fills correctly.
          // Committing to one level meant a payload with data present but
          // ord_status on the envelope failed the hasErFields test below, so the
          // fill branch never ran and no row was ever built. Merge once, inner wins.
          const rawInner = (msg.data ?? {}) as Record<string, unknown>;
          const inner    = { ...(msg as Record<string, unknown>), ...rawInner };

          // Distinguish a NexRisk NOS_SENT notification from a FIX ExecutionReport.
          // Only the ER carries exec_type / ord_status; prefer the explicit
          // discriminator when C++ sends it. Read `type` from the inner payload
          // only — the merged view inherits msg.type, which is always
          // 'EXECUTION_REPORT' and would defeat the check.
          // Documented as `clord_id`, published as `cl_ord_id`. Accept either.
          const erClordId = String(inner.cl_ord_id ?? inner.clord_id ?? '');

          const innerType   = rawInner.type as string | undefined;
          const hasErFields = inner.exec_type !== undefined || inner.ord_status !== undefined;
          const isNosSent   = innerType === 'NOS_SENT'
            || (!hasErFields && !!erClordId && !inner.trade_report_id);

          // ── NOS sent outbound ──────────────────────────────────
          if (isNosSent) {
            if (!erClordId) return;   // no key — cannot build or correlate a row
            const nosRecord: NosRecord = {
              clord_id:  erClordId,
              symbol:    inner.symbol     as string,
              side:      inner.side       as string,
              order_qty: inner.qty        as number ?? 0,
              ord_type:  (inner.ord_type as string) === 'LIMIT' ? '2' : (inner.ord_type as string) === 'STOP' ? '3' : '1',
              tif:       (inner.tif as string) === 'DAY' ? '0' : (inner.tif as string) === 'IOC' ? '3' : (inner.tif as string) === 'FOK' ? '4' : '1',
              nos_ts:    inner.timestamp_ms as number ?? Date.now(),
              // Stamped by BFF from authenticated session. Null for orders from
              // legacy paths; could be "Hedge Engine" for auto-hedges.
              submitted_by: (inner.submitted_by as string | null | undefined) ?? null,
            };
            // Key by clord_id (unique per order). symbol|side collides under
            // rapid same-symbol/side orders and strands pending rows.
            nosMapRef.current.set(nosRecord.clord_id, nosRecord);

            const pendingRow = buildPendingRow(nosRecord, lp_id);
            const existing = rowMapRef.current.get(pendingRow.trade_report_id);
            rowMapRef.current.set(pendingRow.trade_report_id, pendingRow);
            // Keep clordIdMapRef in sync — hedge.fill uses clord_id to find the row
            clordIdMapRef.current.set(nosRecord.clord_id, pendingRow.trade_report_id);
            if (existing) {
              gridRef.current?.api?.applyTransactionAsync({ update: [pendingRow] });
            } else {
              gridRef.current?.api?.applyTransactionAsync({ add: [pendingRow], addIndex: 0 });
            }
            // Refresh the detail panel if this row is currently selected —
            // AG Grid mutations don't trigger React re-renders on their own.
            if (selectedIdRef.current === pendingRow.trade_report_id) {
              setSelectedRow(pendingRow);
            }

          // ── FIX ExecutionReport — the fill ─────────────────────
          // Fills are built from the ER, not the AE. The ER is the only message
          // carrying a per-order key (tag 11); TE omits it on the AE and tag 37 is
          // the position id under netting. Keyed on cl_ord_id, this updates the
          // NOS row in place rather than creating a second row beside it.
          } else if (hasErFields && erClordId) {
            const clordId = erClordId;
            const rowId   = `pending_${clordId}`;
            const nosRecord = nosMapRef.current.get(clordId) ?? null;
            const existing  = rowMapRef.current.get(rowId);

            const row = buildRowFromExecReport(inner, lp_id, nosRecord, existing);

            if (row.rule_id === null) {
              const match = hedgeRuleMapRef.current.get(clordId)
                ?? pendingHedgeFillsRef.current.get(clordId);
              if (match) { row.rule_id = match.rule_id; row.rule_name = match.rule_name; }
            }
            pendingHedgeFillsRef.current.delete(clordId);

            rowMapRef.current.set(rowId, row);
            clordIdMapRef.current.set(clordId, rowId);

            if (existing) {
              gridRef.current?.api?.applyTransactionAsync({ update: [row] });
            } else {
              gridRef.current?.api?.applyTransactionAsync({ add: [row], addIndex: 0 });
            }
            if (selectedIdRef.current === rowId) setSelectedRow(row);

          // ── AE fill from TE ────────────────────────────────────
          // Kept unconditional. TE omits tag 11 on the AE, so requiring a ClOrdID
          // here silently dropped every DOM Trader fill — the orders executed and
          // no row was ever built. An uncorrelated row showing "-" and "Manual" is
          // far better than an invisible fill.
          } else if (inner.trade_report_id) {
            const ae = inner as unknown as TradeCaptureWsEvent['data'];
            // Correlate by clord_id (embedded on the AE by C++ LookupNOS).
            // Exact 1:1 match — no symbol|side guessing, no time window.
            const clordId  = ae.cl_ord_id || '';
            const nosRecord = clordId ? (nosMapRef.current.get(clordId) ?? null) : null;

            if (clordId) {
              const pendingKey = `pending_${clordId}`;
              const pendingRow = rowMapRef.current.get(pendingKey);
              if (pendingRow) {
                rowMapRef.current.delete(pendingKey);
                gridRef.current?.api?.applyTransactionAsync({ remove: [pendingRow] });
              }
              nosMapRef.current.delete(clordId);
            }

            const row = buildRowFromAE(ae, lp_id, nosRecord);
            // Enrich with strategy name from hedge records map (ground truth)
            if (row.clord_id) {
              const match = hedgeRuleMapRef.current.get(row.clord_id);
              if (match) { row.rule_id = match.rule_id; row.rule_name = match.rule_name; }
            }
            const existing = rowMapRef.current.get(row.trade_report_id);
            // Keep clordIdMapRef in sync — hedge.fill uses clord_id to find the row
            if (row.clord_id) clordIdMapRef.current.set(row.clord_id, row.trade_report_id);

            // Apply any pending hedge.fill that arrived before this fill row
            const pendingFill = row.clord_id ? pendingHedgeFillsRef.current.get(row.clord_id) : undefined;
            if (pendingFill) {
              row.rule_id   = pendingFill.rule_id;
              row.rule_name = pendingFill.rule_name;
              pendingHedgeFillsRef.current.delete(row.clord_id!);
            }

            rowMapRef.current.set(row.trade_report_id, row);
            if (existing) {
              gridRef.current?.api?.applyTransactionAsync({ update: [row] });
            } else {
              gridRef.current?.api?.applyTransactionAsync({ add: [row], addIndex: 0 });
            }
            // Refresh the detail panel if this row — or the pending row it
            // replaced — is currently selected.
            const selId = selectedIdRef.current;
            if (selId && (selId === row.trade_report_id
                || (row.clord_id && selId === `pending_${row.clord_id}`))) {
              setSelectedRow(row);
            }
          } else {
            // Matched no branch — a shape we do not recognise. Previously this was
            // dropped in silence, which is how DOM orders went missing with no
            // error anywhere. Log the payload keys so a mismatch is visible.
            console.warn('[ExecReport] unhandled EXECUTION_REPORT payload', {
              keys: Object.keys(inner), inner,
            });
          }

        // ── TRADE_CAPTURE_REPORT — TE fill (35=AE) ───────────
        // Published on "trade" ZMQ topic → nexrisk_service sets type=TRADE_CAPTURE_REPORT
        // Fields are under msg.data (unwrapped from inner data by NexRiskService.cpp)
        } else if (msg.type === 'TRADE_CAPTURE_REPORT') {
          const ae = msg.data as unknown as TradeCaptureWsEvent['data'];
          if (!ae?.trade_report_id) return;

          const lp_id = msg.lp_id as string ?? '';
          // Correlate by clord_id (embedded on the AE by C++ LookupNOS).
          // Exact 1:1 match — no symbol|side guessing, no time window.
          const clordId   = ae.cl_ord_id || '';
          const nosRecord = clordId ? (nosMapRef.current.get(clordId) ?? null) : null;

          if (clordId) {
            const pendingKey = `pending_${clordId}`;
            const pendingRow = rowMapRef.current.get(pendingKey);
            if (pendingRow) {
              rowMapRef.current.delete(pendingKey);
              gridRef.current?.api?.applyTransactionAsync({ remove: [pendingRow] });
            }
            nosMapRef.current.delete(clordId);
          }

          const row = buildRowFromAE(ae, lp_id, nosRecord);
          // Enrich with strategy name from hedge records map (ground truth)
          if (row.clord_id) {
            const match = hedgeRuleMapRef.current.get(row.clord_id);
            if (match) { row.rule_id = match.rule_id; row.rule_name = match.rule_name; }
          }
          const existing = rowMapRef.current.get(row.trade_report_id);
          // Keep clordIdMapRef in sync — hedge.fill uses clord_id to find the row
          if (row.clord_id) clordIdMapRef.current.set(row.clord_id, row.trade_report_id);

          // Apply any pending hedge.fill that arrived before this fill row
          const pendingFill2 = row.clord_id ? pendingHedgeFillsRef.current.get(row.clord_id) : undefined;
          if (pendingFill2) {
            row.rule_id   = pendingFill2.rule_id;
            row.rule_name = pendingFill2.rule_name;
            pendingHedgeFillsRef.current.delete(row.clord_id!);
          }

          rowMapRef.current.set(row.trade_report_id, row);
          if (existing) {
            gridRef.current?.api?.applyTransactionAsync({ update: [row] });
          } else {
            gridRef.current?.api?.applyTransactionAsync({ add: [row], addIndex: 0 });
          }
          // Refresh the detail panel if this row — or the pending row it
          // replaced — is currently selected.
          const selIdTcr = selectedIdRef.current;
          if (selIdTcr && (selIdTcr === row.trade_report_id
              || (row.clord_id && selIdTcr === `pending_${row.clord_id}`))) {
            setSelectedRow(row);
          }
        } else if (msg.type === 'SESSION_STATE_CHANGE' || msg.type === 'SESSION_LOGON' || msg.type === 'SESSION_LOGOUT') {
          const lp_id    = msg.lp_id as string | undefined;
          const newState = (msg.session_state ?? msg.state ?? (msg.type === 'SESSION_LOGON' ? 'LOGGED_ON' : 'DISCONNECTED')) as string;
          if (!lp_id) return;
          setLpStatuses(prev => {
            const exists = prev.some(lp => lp.lp_id === lp_id);
            if (exists) return prev.map(lp => lp.lp_id === lp_id ? { ...lp, trading_session: newState } : lp);
            return [...prev, { lp_id, trading_session: newState }];
          });

        // POSITION_REPORT / POSITION_CLOSED intentionally not handled —
        // ExecutionReport does not render net positions.
        }

      } catch { /* ignore parse errors */ }
    };

    ws.onclose = () => {
      if (!mountedRef.current) return;
      if (retryRef.current >= WS_MAX_RETRIES) {
        setWsStatus('error');
        setWsError('WebSocket disconnected. Click ↻ to reconnect.');
        return;
      }
      setWsStatus('reconnecting');
      const delay = Math.min(1000 * 2 ** retryRef.current, 30_000);
      retryRef.current++;
      timerRef.current = setTimeout(() => { if (mountedRef.current) connectWs(); }, delay);
    };

    ws.onerror = () => { ws.close(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reconnect = useCallback(() => {
    retryRef.current = 0;
    wsRef.current?.close();
    if (timerRef.current) clearTimeout(timerRef.current);
    connectWs();
  }, [connectWs]);

  useEffect(() => {
    mountedRef.current = true;
    connectWs();
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      wsRef.current?.close();
    };
  }, [connectWs]);

  // ── Hedge records map — loads on mount, refreshes every 60s ──
  // Ground truth for strategy names AND for order state. Keyed by clord_id.
  // Enriches seed and live rows, and reconciles rows the WS could not resolve.
  useEffect(() => {
    // Page fully every time, but at 60s rather than 30s.
    //
    // The previous revision polled only page 1 to cut load. That also shrank the
    // enrichment window to the 200 most recent records against a 2,000-row grid,
    // so most rows could never be matched — it gave away the thing the poll exists
    // to do. Halving the frequency instead keeps the full window at roughly the
    // same request volume.
    const loadHedgeRecords = async () => {
      try {
        const records = await fetchHedgeRecordsPaged(SEED_ROW_CAP);
        if (!records) return;

        const map = new Map<string, { rule_id: number; rule_name: string | null }>();
        // Notional (LP units) keyed by clord_id. The NOS and AE report MT5 lots, so
        // a live row shows 0.1 where the record says 10,000 for the same order. The
        // Qty and Fill Qty columns are notional, so the record is the correct source
        // and lots are never displayed.
        const volMap = new Map<string, { order_qty: number; fill_qty: number }>();
        for (const r of records) {
          const clord = String(r.clord_id ?? '');
          if (!clord) continue;
          if (r.rule_id != null) {
            map.set(clord, { rule_id: Number(r.rule_id), rule_name: (r.rule_name as string) ?? null });
          }
          volMap.set(clord, {
            order_qty: parseFloat(String(r.hedge_volume_lp ?? '0')) || 0,
            fill_qty:  parseFloat(String(r.lp_fill_volume_lp ?? '0')) || 0,
          });
        }
        hedgeRuleMapRef.current = map;

        const updates: ExecutionReportRow[] = [];

        // 1. Reconcile rows stuck at PENDING.
        //
        // A pending row is created on NOS_SENT keyed `pending_<clord_id>` and is
        // retired when the AE arrives carrying that same cl_ord_id. When the AE
        // arrives without it, nothing can retire the row and it sits at PENDING
        // showing a filled order as unfilled — the same missing field that leaves
        // User as "—" and the strategy name blank.
        //
        // hedge_records is authoritative and already fetched here, so rebuild those
        // rows from it. buildRowFromHedge derives the identical `pending_<clord_id>`
        // key, so this updates in place rather than duplicating, and restores fill
        // price, fill time, round-trip and LP position id along with the state.
        //
        // This is a safety net, not the fix. It closes within one poll interval;
        // stamping cl_ord_id on the AE resolves it instantly and is the real answer.
        for (const r of records) {
          const clord = String(r.clord_id ?? '');
          if (!clord) continue;
          const existing = rowMapRef.current.get(`pending_${clord}`);
          if (!existing || existing.te_status !== 'PENDING') continue;
          const state = String(r.hedge_state ?? '');
          if (!state || state === 'PENDING') continue;   // genuinely still pending
          const rebuilt = buildRowFromHedge(
            hedgeRecordToExecutionRow(r),
            String(r.hedging_lp_id ?? existing.lp_id ?? ''),
          );
          rowMapRef.current.set(rebuilt.trade_report_id, rebuilt);
          updates.push(rebuilt);
        }

        // 2. Enrich rows from the authoritative record: strategy name where it is
        //    still missing, and notional quantities in place of the lots the NOS
        //    and AE report. Only push an update when something actually changed,
        //    so this does not churn the grid every 30s.
        for (const [, row] of rowMapRef.current) {
          if (!row.clord_id) continue;
          let next = row;

          if (next.rule_id === null) {
            const match = map.get(next.clord_id);
            if (match) next = { ...next, rule_id: match.rule_id, rule_name: match.rule_name };
          }

          const vol = volMap.get(next.clord_id);
          if (vol) {
            if (vol.order_qty > 0 && next.order_qty !== vol.order_qty) {
              next = { ...next, order_qty: vol.order_qty };
            }
            if (vol.fill_qty > 0 && next.fill_qty !== vol.fill_qty) {
              next = { ...next, fill_qty: vol.fill_qty };
            }
          }

          if (next !== row) {
            rowMapRef.current.set(row.trade_report_id, next);
            updates.push(next);
          }
        }

        if (updates.length > 0) {
          gridRef.current?.api?.applyTransactionAsync({ update: updates });
        }
      } catch { /* non-fatal */ }
    };

    loadHedgeRecords();
    const t = setInterval(loadHedgeRecords, 60_000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Throttled chart/stats sync (3s) ─────────────────────────
  // The grid is driven imperatively via applyTransaction; rowMapRef is the live
  // source of truth. This samples it every 3s into React state so the charts and
  // stats bar stay current without re-rendering the page on every WS tick.
  // ── Throttled chart/stats sync (3s) ─────────────────────────
  // Samples rowMapRef into React state for charts/stats, but only when the
  // data actually changed. Skipping setRows when idle prevents the charts
  // (recharts) from re-rendering every 3s — those re-renders accumulate event
  // listeners that made navigation-away take tens of seconds.
  useEffect(() => {
    let lastCount = -1;
    let lastTopId = '';
    // Cap live grid size so AG-Grid teardown stays fast. Raised from 500: the burst
    // test fires 100 orders/sec for 5s, so 500 orders arrive inside the window and the
    // old cap evicted the earlier half of the run before it could be read. Phantom-row
    // removal (see the EXECUTION_REPORT handler) roughly halves rows per order, so
    // 2000 leaves comfortable headroom without returning to unbounded growth.
    const MAX_GRID_ROWS = SEED_ROW_CAP;
    const sync = () => {
      const map = rowMapRef.current;
      let snapshot = Array.from(map.values());
      snapshot.sort((a, b) =>
        parseTimestamp(b.transact_time) - parseTimestamp(a.transact_time)
      );

      // Evict oldest rows beyond the cap from BOTH the grid and rowMapRef.
      // Without this the grid grows unbounded; AG-Grid then attaches a listener
      // per cell that never gets removed until unmount, making navigation-away
      // take tens of seconds (destroyBeans -> removeEventListener storm).
      if (snapshot.length > MAX_GRID_ROWS) {
        const keep = snapshot.slice(0, MAX_GRID_ROWS);
        const drop = snapshot.slice(MAX_GRID_ROWS);
        if (drop.length > 0) {
          try { gridRef.current?.api?.applyTransactionAsync({ remove: drop }); } catch { /* grid not ready */ }
          for (const r of drop) {
            map.delete(r.trade_report_id);
            // Purge the secondary indexes too. Without this, clordIdMapRef and
            // pendingHedgeFillsRef retain an entry per evicted row for the lifetime
            // of the page — unbounded growth over a sustained run, and stale clord_id
            // entries that resolve hedge.fill events to rows that no longer exist.
            if (r.clord_id) {
              clordIdMapRef.current.delete(r.clord_id);
              pendingHedgeFillsRef.current.delete(r.clord_id);
            }
          }
        }
        snapshot = keep;
      }

      const count = snapshot.length;
      const topId = count ? snapshot[0].trade_report_id : '';
      if (count === lastCount && topId === lastTopId) return;
      lastCount = count;
      lastTopId = topId;
      setRows(snapshot);
    };
    const t = setInterval(sync, 3000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape key → close side panel
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedRow(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // ── Filtered rows (by selected LP) ─────────────────────────
  // Order Flow reads the live row map directly (not the 3s grid sync).
  const selectedLpRef = useRef(selectedLp);
  selectedLpRef.current = selectedLp;
  const getOrderFlowRows = useCallback((): ExecutionReportRow[] => {
    const lp = selectedLpRef.current;
    const out: ExecutionReportRow[] = [];
    for (const r of rowMapRef.current.values()) if (lp === 'all' || r.lp_id === lp) out.push(r);
    return out;
  }, []);
  const orderFlowLpLabel = selectedLp === 'all'
    ? 'all LPs'
    : (lpStatuses.find(l => l.lp_id === selectedLp)?.lp_name ?? selectedLp);

  const filteredRows = useMemo(() =>
    selectedLp === 'all' ? rows : rows.filter(r => r.lp_id === selectedLp),
    [rows, selectedLp]
  );

  // ── Stats (derived from filtered rows) ──────────────────────
  const stats = useMemo(() => {
    const filled = filteredRows.filter(r => FILLED_STATES.has(r.te_status));
    const rts = filled
      .map(r => r.round_trip_ms)
      .filter((v): v is number => v !== null);
    const longCount  = filled.filter(r => r.side === 'BUY').length;
    const shortCount = filled.filter(r => r.side === 'SELL').length;
    const volume     = filled.reduce((s, r) => s + (r.fill_qty ?? 0), 0);

    return {
      positionCount:  filled.length,
      longCount,
      shortCount,
      volume,
      avgRt:          rts.length ? Math.round(rts.reduce((s, v) => s + v, 0) / rts.length) : null,
      bestRt:         rts.length ? Math.min(...rts)  : null,
      worstRt:        rts.length ? Math.max(...rts)  : null,
      rejectionCount: 0,
      rejectionPct:   0,
    };
  }, [filteredRows]);

  // ══════════════════════════════════════════════════════════════
  // COLUMN DEFINITIONS
  // ══════════════════════════════════════════════════════════════
  const columnDefs = useMemo<(ColDef<ExecutionReportRow> | ColGroupDef<ExecutionReportRow>)[]>(() => [

    // ── EXECUTION TYPE — pinned left, first column ─────────────────
    {
      headerName: 'Execution Type',
      children: [
        {
          field: 'rule_name',
          headerName: 'Execution Type',
          headerTooltip: 'Hedging strategy that triggered this order — Auto if rule-driven, Manual if DOM/direct',
          width: 180,
          pinned: 'left',
          filter: 'agSetColumnFilter',
          cellRenderer: (p: { value: string | null; data?: ExecutionReportRow }) => {
            if (!p.value) {
              return <span style={{ color: '#aaa', fontSize: 11, fontStyle: 'italic' }}>Manual</span>;
            }
            return (
              <span
                onClick={() => {
                  if (p.data?.rule_id != null) {
                    sessionStorage.setItem('nexrisk:hedging:selected', String(p.data.rule_id));
                    navigate('/hedging-strategies');
                  }
                }}
                title={`Rule #${p.data?.rule_id} — click to open in Hedging Strategies`}
                style={{
                  color: '#EDE3EC',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                  textDecoration: 'underline',
                  whiteSpace: 'nowrap' as const,
                }}
              >
                {p.value}
              </span>
            );
          },
        },
      ],
    },

    // ── META ─────────────────────────────────────────────────────
    {
      headerName: 'Meta',
      children: [
        {
          field: 'lp_id',
          headerName: 'LP',
          headerTooltip: 'Liquidity Provider',
          width: 100,
          filter: 'agSetColumnFilter',
        },
        {
          field: 'transact_time',
          headerName: 'TE Filled',
          headerTooltip: 'Time TE confirmed the fill (35=AE received). Sorted by send time.',
          width: 110,
          sort: 'desc',
          filter: 'agTextColumnFilter',
          valueFormatter: (p: ValueFormatterParams) => (p.data as ExecutionReportRow)?.fill_time ?? '—',
          cellStyle: { fontFamily: 'monospace', fontSize: 11 },
        },
        {
          field: 'trade_report_id',
          headerName: 'Trade Rpt ID',
          headerTooltip: 'TradeReportRefID (tag 571)',
          width: 180,
          filter: 'agTextColumnFilter',
          cellStyle: { fontFamily: 'monospace', fontSize: 11 },
          hide: true,
        },
      ],
    },

    // ── TIMING ──────────────────────────────────────────────────
    {
      headerName: 'Timing',
      children: [
        {
          field: 'nos_time',
          headerName: 'NOS Sent',
          headerTooltip: 'Time we sent the NewOrderSingle — correlated from outbound NOS',
          width: 90,
          filter: 'agTextColumnFilter',
          cellStyle: { fontFamily: 'monospace' },
        },
        {
          field: 'te_status',
          headerName: 'LP Status',
          headerTooltip: 'FILLED = 35=AE received; PENDING = NOS sent, no AE yet',
          width: 100,
          filter: 'agSetColumnFilter',
          filterParams: { values: ['FILLED', 'HEDGED', 'PARTIAL', 'PENDING', 'FAILED', 'REJECTED', 'CANCELLED', 'ERROR', 'B_BOOK', 'ESCALATED', 'CLOSED', 'CLOSING', 'UNKNOWN'] },
          cellRenderer: (p: { value: ExecutionReportRow['te_status'] }) => {
            const colors: Record<string, string> = {
              'FILLED':   '#66e07a',
              // hedge_records.hedge_state values reach this column via the seed and
              // the reconciliation path. Without entries here they fell through to
              // the grey default and read as unrecognised.
              'HEDGED':   '#66e07a',
              'ESCALATED': '#e0a020',
              'CANCELLED': '#8a8a8a',
              'PARTIAL':  '#8fcf9f',
              'PENDING':  '#e0a020',
              'FAILED':   '#ff5c5c',
              'REJECTED': '#ff5c5c',
              'ERROR':    '#ff8c3c',
              'B_BOOK':   '#49b3b3',
              'CLOSED':   '#f0b0f0ff',
              'CLOSING':  '#f5ececff',
              'UNKNOWN':  '#c5c0c0ff',
            };
            const fg = colors[p.value] ?? '#777';
            return (
              <span style={{ color: fg, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em' }}>
                {p.value}
              </span>
            );
          },
        },
        {
          field: 'round_trip_ms',
          headerName: 'RT (ms)',
          headerTooltip: 'Round trip: AE/confirm receive time − NOS sent time',
          width: 80,
          filter: 'agNumberColumnFilter',
          type: 'rightAligned',
          cellRenderer: (p: { value: number | null }) => {
            if (p.value === null || p.value === undefined)
              return <span style={{ color: '#555' }}>—</span>;
            const color = p.value <= 200 ? '#66e07a' : p.value <= 600 ? '#e0a020' : '#ff5c5c';
            return <span style={{ color, fontWeight: 700 }}>{p.value}</span>;
          },
        },
      ],
    },

    // ── ORDER ────────────────────────────────────────────────────
    {
      headerName: 'Order',
      children: [
        {
          field: 'user',
          headerName: 'User',
          width: 90,
          filter: 'agSetColumnFilter',
        },
        {
          field: 'order_id',
          headerName: 'LP Order ID',
          headerTooltip: 'LP order ID (tag 37)',
          width: 100,
          filter: 'agTextColumnFilter',
          cellStyle: { fontFamily: 'monospace' },
        },
        {
          field: 'symbol',
          headerName: 'Symbol',
          headerTooltip: 'Instrument (tag 55)',
          width: 80,
          pinned: 'left',
          filter: 'agSetColumnFilter',
        },
        {
          field: 'side',
          headerName: 'Side',
          headerTooltip: 'Tag 54',
          width: 60,
          filter: 'agSetColumnFilter',
          filterParams: { values: ['BUY', 'SELL'] },
          cellRenderer: (p: { value: 'BUY' | 'SELL' }) => (
            <span style={{ color: p.value === 'BUY' ? '#49b3b3' : '#e0a020', fontWeight: 700 }}>
              {p.value}
            </span>
          ),
        },
        {
          field: 'ord_type',
          headerName: 'Type',
          headerTooltip: 'Order type (tag 40) — from NOS',
          width: 70,
          filter: 'agSetColumnFilter',
          filterParams: { values: ['MKT', 'LMT', 'STP', 'STPLMT'] },
        },
        {
          field: 'tif',
          headerName: 'TIF',
          headerTooltip: 'Time in force (tag 59) — from NOS',
          width: 60,
          filter: 'agSetColumnFilter',
          filterParams: { values: ['DAY', 'GTC', 'IOC', 'FOK', 'GTD'] },
        },
      ],
    },

    // ── FILL ─────────────────────────────────────────────────────
    {
      headerName: 'Fill',
      children: [
        {
          field: 'fill_qty',
          headerName: 'Qty',
          headerTooltip: 'Order quantity (tag 38) — from NOS',
          width: 90,
          filter: 'agNumberColumnFilter',
          type: 'rightAligned',
          valueFormatter: fmtQty,
        },
        {
          field: 'fill_px',
          headerName: 'Fill Px',
          headerTooltip: 'Last price (tag 31)',
          width: 90,
          filter: 'agNumberColumnFilter',
          type: 'rightAligned',
          valueFormatter: fmtPx,
        },
        {
          field: 'fill_qty',
          headerName: 'Fill Qty',
          headerTooltip: 'Last quantity (tag 32)',
          width: 90,
          filter: 'agNumberColumnFilter',
          type: 'rightAligned',
          valueFormatter: fmtQty,
        },
        {
          field: 'commission',
          headerName: 'Commission',
          headerTooltip: 'Commission (tag 12)',
          width: 90,
          filter: 'agNumberColumnFilter',
          type: 'rightAligned',
          valueFormatter: fmtPx,
          hide: true,
        },
      ],
    },

    // ── ROUTING ──────────────────────────────────────────────────
    {
      headerName: 'Routing',
      children: [
        {
          field: 'route',
          headerName: 'Route',
          headerTooltip: 'ExDestination (tag 100)',
          width: 90,
          filter: 'agTextColumnFilter',
        },
        {
          field: 'security_exchange',
          headerName: 'Exchange',
          headerTooltip: 'SecurityExchange (tag 207)',
          width: 80,
          filter: 'agSetColumnFilter',
          hide: true,
        },
        {
          field: 'security_id',
          headerName: 'Sec ID',
          headerTooltip: 'SecurityID (tag 48)',
          width: 80,
          filter: 'agTextColumnFilter',
          hide: true,
        },
        {
          field: 'account',
          headerName: 'Account',
          headerTooltip: 'Account (tag 1)',
          width: 130,
          filter: 'agSetColumnFilter',
        },
      ],
    },

    // ── CLORDID — last column, unpinned ──────────────────────────
    {
      headerName: 'Reference',
      children: [
        {
          field: 'clord_id',
          headerName: 'ClOrdID',
          headerTooltip: 'Our client order ID (tag 11) — correlated from NOS',
          width: 200,
          filter: 'agTextColumnFilter',
          cellStyle: { fontFamily: 'monospace', fontSize: 11 },
          tooltipField: 'clord_id',
        },
      ],
    },
  ], [navigate]);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable:  true,
    filter:    true,
    resizable: true,
    minWidth:  50,
  }), []);

  const rowSelection = useMemo<RowSelectionOptions>(() => ({
    mode: 'singleRow',
    enableClickSelection: true,
  }), []);

  const gridOptions = useMemo<GridOptions<ExecutionReportRow>>(() => ({
    enableAdvancedFilter: true,
    sideBar: {
      toolPanels: ['columns', 'filters'],
      defaultToolPanel: '',
    },
    columnHoverHighlight: true,
    // Every column is sized, not only those on screen.
    suppressColumnVirtualisation: true,
    animateRows: false,
    rowBuffer: 20,
    asyncTransactionWaitMillis: 60,
    suppressMovableColumns: true,
    debounceVerticalScrollbar: true,
    getRowStyle: (params) => {
      if (params.data?.te_status === 'PENDING') return { backgroundColor: '#282000' };
      return undefined;
    },
    statusBar: {
      statusPanels: [
        { statusPanel: 'agTotalAndFilteredRowCountComponent' },
        { statusPanel: 'agSelectedRowCountComponent'        },
        { statusPanel: 'agAggregationComponent'             },
      ],
    },
  }), []);

  // AG Grid sizes columns to rendered content, so running this against an empty
  // grid collapses every column to its minimum — which is what happened whenever
  // the history seed returned nothing. Only size when there are rows, and mark it
  // done so the WS-driven re-size below fires exactly once.
  const autoSizedRef = useRef(false);
  // A column the user resized by hand is left alone from then on.
  const userResizedRef = useRef(false);
  const autoSizeIfRows = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api || userResizedRef.current) return;
    if (api.getDisplayedRowCount() === 0) return;
    api.autoSizeAllColumns(false);   // the header counts too
    autoSizedRef.current = true;
  }, []);
  // Rows land through async transactions (and the grid is hidden until the
  // first one): size again shortly after each batch, debounced.
  const sizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleAutoSize = useCallback(() => {
    if (sizeTimerRef.current) clearTimeout(sizeTimerRef.current);
    sizeTimerRef.current = setTimeout(() => { sizeTimerRef.current = null; autoSizeIfRows(); }, 300);
  }, [autoSizeIfRows]);
  const onColumnResized = useCallback((e: { source?: string; finished?: boolean }) => {
    if (e.finished && e.source === 'uiColumnResized') userResizedRef.current = true;
  }, []);

  // LP filter: the table shows the selected LP's orders (the stats above
  // already did). The grid holds every row; an external filter hides the rest.
  const isExternalFilterPresent = useCallback(() => selectedLpRef.current !== 'all', []);
  const doesExternalFilterPass = useCallback(
    (node: { data?: ExecutionReportRow }) => !!node.data && node.data.lp_id === selectedLpRef.current, []);
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
    scheduleAutoSize();
  }, [selectedLp, scheduleAutoSize]);

  const onGridReady = useCallback((_ev: GridReadyEvent) => {
    setTimeout(autoSizeIfRows, 0);
  }, [autoSizeIfRows]);

  const onFirstDataRendered = useCallback(() => {
    autoSizeIfRows();
  }, [autoSizeIfRows]);

  // Rows usually arrive by WS after the grid is already up, which is after both
  // callbacks above have run. Size once on the first batch that lands.
  useEffect(() => {
    if (autoSizedRef.current || rows.length === 0) return;
    const t = setTimeout(autoSizeIfRows, 150);
    return () => clearTimeout(t);
  }, [rows.length, autoSizeIfRows]);

  // Re-size columns whenever filteredRows changes — covers initial load via
  // applyTransaction (fires after onFirstDataRendered), LP filter switches,
  // and any subsequent row updates. Debounced so rapid WS updates don't thrash.
  

  const onRowClicked = useCallback((params: { data?: ExecutionReportRow }) => {
    if (params.data) {
      // Set the ref eagerly as well as via the effect. The effect only commits after
      // the next render, so a WS frame for this row arriving in that window would miss
      // the guard and leave the detail panel stale until the following frame.
      selectedIdRef.current = params.data.trade_report_id;
      setSelectedRow(params.data);
    }
  }, []);

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
      const rowData = params.node?.data as ExecutionReportRow | undefined;
      return [
        { name: 'View Order Details', action: () => {
          if (rowData) { selectedIdRef.current = rowData.trade_report_id; setSelectedRow(rowData); }
        } },
        'separator',
        'copy',
        'copyWithHeaders',
        'separator',
        { name: 'Export to CSV',   action: () => params.api.exportDataAsCsv()   },
        { name: 'Export to Excel', action: () => params.api.exportDataAsExcel() },
      ];
    },
    []
  );

  const handleCopy = useCallback(() => {
    if (!selectedRow) return;
    const text = [
      '═══════════════════════════════════════',
      'NEXRISK EXECUTION REPORT',
      '═══════════════════════════════════════',
      `${selectedRow.side} ${selectedRow.fill_qty} ${selectedRow.symbol} @ ${selectedRow.fill_px}`,
      `Status    : ${selectedRow.te_status}`,
      `TE OrderID: ${selectedRow.order_id}`,
      `ClOrdID   : ${selectedRow.clord_id || '—'}`,
      '───────────────────────────────────────',
      `NOS sent  : ${selectedRow.nos_time}`,
      `Fill time : ${selectedRow.transact_time ? formatSsMs(selectedRow.transact_time) : '—'}`,
      `Round trip: ${selectedRow.round_trip_ms !== null ? `${selectedRow.round_trip_ms}ms` : '—'}`,
      '───────────────────────────────────────',
      `Account   : ${selectedRow.account}`,
      `Route     : ${selectedRow.route}`,
      `Exchange  : ${selectedRow.security_exchange}`,
      `LP        : ${selectedRow.lp_id}`,
      '───────────────────────────────────────',
      `Generated : ${new Date().toLocaleString('en-GB')}`,
      '═══════════════════════════════════════',
    ].join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [selectedRow]);

  /* HIDDEN: Telegram share handler — hidden per request, not removed
  const handleTelegram = useCallback(() => {
    if (!selectedRow) return;
    const msg = `NEXRISK EXECUTION\n\n${selectedRow.side} ${selectedRow.fill_qty} ${selectedRow.symbol} @ ${selectedRow.fill_px}\nStatus: ${selectedRow.te_status}\nRT: ${selectedRow.round_trip_ms !== null ? `${selectedRow.round_trip_ms}ms` : '—'}\nOrderID: ${selectedRow.order_id}\nLP: ${selectedRow.lp_id}\nTime: ${selectedRow.transact_time}`;
    window.open(`https://t.me/share/url?url=&text=${encodeURIComponent(msg)}`, '_blank', 'width=550,height=450');
  }, [selectedRow]);
  */

  const badge = WS_BADGE[wsStatus];

  // ══════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════
  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: '#232326' }}>

      {/* ── Page Header ─────────────────────────────────────────── */}
      <div className="px-4 py-2 border-b border-[#808080] flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-lg font-semibold text-white">Execution Report</h1>
          <p className="text-xs text-[#999]">FIX Bridge order blotter</p>
        </div>

        <div className="flex items-center gap-5 text-xs flex-wrap">

          {/* LP selector dropdown */}
          {lpStatuses.length > 0 && (
            <>
              <div className="flex items-center gap-2">
              <span className="text-[#999] text-xs">Liquidity Provider:</span>
              <select
                value={selectedLp}
                onChange={e => setSelectedLp(e.target.value)}
                className="w-[200px] bg-[#232225] border border-[#606060] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#49b3b3]"
              >
                <option value="all">All Liquidity Providers</option>
                {lpStatuses.map(lp => (
                  <option key={lp.lp_id} value={lp.lp_id}>
                    {lp.lp_name ?? lp.lp_id}
                    {lp.trading_session === 'DISCONNECTED' ? ' — Disconnected' : ''}
                  </option>
                ))}
              </select>
              </div>
              <div className="w-px h-4 bg-[#505050]" />
            </>
          )}

          <div>
            <span className="text-[#999]">Positions:</span>
            <span className="ml-1 font-mono text-white">{stats.positionCount}</span>
          </div>
          <div className="w-px h-4 bg-[#505050]" />

          <div>
            <span className="text-[#999]">Long / Short:</span>
            <span className="ml-1 font-mono">
              <span className="text-[#49b3b3]">{stats.longCount}</span>
              <span className="text-[#505050]"> / </span>
              <span className="text-[#e0a020]">{stats.shortCount}</span>
            </span>
          </div>

          <div>
            <span className="text-[#999]">Vol:</span>
            <span className="ml-1 font-mono text-white">{stats.volume.toLocaleString()}</span>
          </div>

          <div className="w-px h-4 bg-[#505050]" />

          <div>
            <span className="text-[#999]">Avg RT:</span>
            <span className="ml-1 font-mono text-white">
              {stats.avgRt !== null ? `${stats.avgRt}ms` : '—'}
            </span>
          </div>
          <div>
            <span className="text-[#999]">Best RT:</span>
            <span className="ml-1 font-mono text-[#66e07a]">
              {stats.bestRt !== null ? `${stats.bestRt}ms` : '—'}
            </span>
          </div>
          <div>
            <span className="text-[#999]">Worst RT:</span>
            <span className="ml-1 font-mono text-[#ff5c5c]">
              {stats.worstRt !== null ? `${stats.worstRt}ms` : '—'}
            </span>
          </div>

          <div className="w-px h-4 bg-[#505050]" />

          <div>
            <span className="text-[#999]">Rejections:</span>
            <span className="ml-1 font-mono text-[#ff5c5c]">{stats.rejectionCount}</span>
          </div>
          <div>
            <span className="text-[#999]">Rejection %:</span>
            <span className="ml-1 font-mono text-[#ff5c5c]">
              {stats.rejectionPct.toFixed(1)}%
            </span>
          </div>

          <div className="w-px h-4 bg-[#505050]" />

          {/* WS status badge */}
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: badge.color }} />
            <span className="font-mono text-[10px]" style={{ color: badge.color }}>{badge.label}</span>
          </div>
        </div>
      </div>

      {/* ── Error banner ─────────────────────────────────────────── */}
      {wsStatus === 'error' && wsError && (
        <div className="px-4 py-2 bg-[#3a1f1f] border-b border-[#ff5c5c] text-xs text-[#ff5c5c] flex items-center justify-between flex-shrink-0">
          <span>⚠ {wsError}</span>
          <button onClick={reconnect} className="ml-4 underline hover:no-underline">Reconnect</button>
        </div>
      )}

      {/* ── Content: Grid + Side Panel ───────────────────────────── */}
      <div className="flex-1 flex overflow-hidden relative">

        {/* Grid column — grid above, charts below */}
        <div
          className="flex flex-col overflow-hidden transition-all duration-300"
          style={{ width: selectedRow ? 'calc(100% - 400px)' : '100%' }}
        >
          {/* Grid area */}
          <div className="flex-1 flex flex-col overflow-hidden p-2">
            {/* Connecting */}
            {wsStatus === 'connecting' && (
              <div className="flex-1 flex items-center justify-center text-[#999] text-sm">
                <span className="font-mono">Connecting to FIX Bridge…</span>
              </div>
            )}

            {/* Reconnecting with no rows */}
            {wsStatus === 'reconnecting' && rows.length === 0 && (
              <div className="flex-1 flex items-center justify-center text-[#e0a020] text-sm">
                <span className="font-mono">Reconnecting…</span>
              </div>
            )}

            {/* Permanently disconnected */}
            {wsStatus === 'error' && rows.length === 0 && (
              <div className="flex-1 flex flex-col items-center justify-center gap-3">
                <p className="text-[#ff5c5c] text-sm font-mono">Connection lost</p>
                <button
                  onClick={reconnect}
                  className="text-xs text-[#999] border border-[#444] rounded px-3 py-1 hover:text-white hover:border-[#888] transition-colors"
                >
                  ↻ Reconnect
                </button>
              </div>
            )}

            {/* Live but no orders yet */}
            {wsStatus === 'live' && rows.length === 0 && (
              <div className="flex-1 flex flex-col items-center justify-center text-center gap-2">
                <p className="text-[#999] text-sm">{seedError ? 'Order history unavailable' : 'Waiting for orders'}</p>
                <p className="text-[#666] text-xs">
                  {seedError ?? 'Orders will appear here as the FIX Bridge submits them to an LP.'}
                </p>
              </div>
            )}

            {/* AG Grid */}
            <div style={{ flex: 1, width: '100%', display: rows.length > 0 ? undefined : 'none' }}>
              <AgGridReact<ExecutionReportRow>
                ref={gridRef}
                theme={gridTheme}
                rowData={undefined}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                gridOptions={gridOptions}
                rowHeight={26}
                headerHeight={36}
                groupHeaderHeight={28}
                getRowId={(p) => p.data.trade_report_id}
                rowSelection={rowSelection}
                cellSelection={{ enableHeaderHighlight: true }}
                getContextMenuItems={getContextMenuItems}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowClicked={onRowClicked}
                isExternalFilterPresent={isExternalFilterPresent}
                doesExternalFilterPass={doesExternalFilterPass}
                onAsyncTransactionsFlushed={scheduleAutoSize}
                onColumnResized={onColumnResized}
              />
            </div>
          </div>

          {/* ── Analytics Charts Strip ─────────────────────────── */}
          <div
            className={clsx(
              'border-t border-[#808080] flex-shrink-0 transition-all duration-300 overflow-hidden',
              chartsCollapsed ? 'h-[40px]' : 'h-[300px]'
            )}
            style={{ backgroundColor: '#232326' }}
          >
            {/* Toggle bar — exact BBookCharts pattern */}
            <div className="flex justify-center gap-2 py-1 shrink-0">
              <button
                onClick={() => setChartsCollapsed(c => !c)}
                className="flex items-center gap-1 px-3 py-1 text-xs text-white bg-[#232225] border border-[#808080] rounded hover:bg-[#3a3a3c] transition-colors"
              >
                <svg
                  className={`w-4 h-4 transition-transform ${chartsCollapsed ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {chartsCollapsed ? 'Expand Charts' : 'Collapse Charts'}
              </button>
              <button
                onClick={() => setShowOrderFlow(true)}
                className="flex items-center gap-1 px-3 py-1 text-xs text-white bg-[#232225] border border-[#808080] rounded hover:bg-[#3a3a3c] transition-colors"
                title="B-Book and Execution Report order landings, live"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 17l5-6 4 3 5-7 4 4" />
                </svg>
                Order Flow
              </button>
            </div>

            {/* Analytics charts — 4-column grid matching BBookCharts layout */}
            {!chartsCollapsed && (
              <div className="grid grid-cols-4 gap-2 px-2 pb-2" style={{ height: 'calc(100% - 40px)' }}>
                <LatencyOverTimeChart rows={filteredRows} />
                <LatencyByLPChart    rows={filteredRows} />
                <OrdersByStatusChart rows={filteredRows} />
                <VolumeBySymbolChart rows={filteredRows} />
              </div>
            )}
          </div>
        </div>

        {/* ── Order Flow overlay ─────────────────────────────────── */}
        {showOrderFlow && (
          <div className="absolute inset-0 z-30">
            <OrderFlowChart
              getRows={getOrderFlowRows}
              lpLabel={orderFlowLpLabel}
              onClose={() => setShowOrderFlow(false)}
            />
          </div>
        )}

        {/* ── Side Panel ─────────────────────────────────────────── */}
        <div
          className={clsx(
            'absolute right-0 top-0 bottom-0 flex flex-col border-l border-[#404040]',
            'transition-transform duration-300',
            selectedRow ? 'translate-x-0' : 'translate-x-full'
          )}
          style={{ width: 400, backgroundColor: '#252427' }}
        >
          {selectedRow && (
            <>
              {/* Panel header */}
              <div className="px-4 py-3 border-b border-[#404040] flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="text-sm font-semibold"
                    style={{ color: selectedRow.side === 'BUY' ? '#49b3b3' : '#e0a020' }}
                  >
                    {selectedRow.side} {selectedRow.symbol}
                  </span>
                  <span className="text-[#505050]">—</span>
                  <span className="text-xs text-[#999] truncate">{selectedRow.te_status}</span>
                  {/* Copy + Telegram */}
                  <div className="flex items-center gap-1 ml-1">
                    <button
                      onClick={handleCopy}
                      className="p-1 rounded hover:bg-[#3a383c] transition-colors"
                      title="Copy to clipboard"
                    >
                      {copied
                        ? <span style={{ color: '#66e07a' }}><CheckIcon /></span>
                        : <span style={{ color: '#666' }}><CopyIcon /></span>
                      }
                    </button>
                    {/* HIDDEN: Telegram share button — hidden per request, not removed
                    <button
                      onClick={handleTelegram}
                      className="p-1 rounded hover:bg-[#3a383c] transition-colors"
                      title="Share via Telegram"
                    >
                      <span style={{ color: '#666' }}><TelegramIcon /></span>
                    </button>
                    */}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedRow(null)}
                  className="text-[#555] hover:text-white text-xl leading-none transition-colors flex-shrink-0 ml-2"
                  title="Close (Esc)"
                >
                  ×
                </button>
              </div>

              {/* Panel body — plain-English explanation */}
              <div className="flex-1 overflow-y-auto p-4">
                <pre
                  className="font-mono leading-relaxed whitespace-pre-wrap select-text"
                  style={{ fontSize: 11, color: '#bbb' }}
                >
                  {generateExplanation(selectedRow)}
                </pre>
              </div>

              {/* Panel footer */}
              <div className="px-4 py-2 border-t border-[#404040] flex-shrink-0 flex items-center justify-between">
                <span className="text-[10px] text-[#444] font-mono">
                  {selectedRow.trade_report_id}
                </span>
                <span className="text-[10px] text-[#444] font-mono">
                  {selectedRow.lp_id}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExecutionReportPage;